import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { RISK_COLORS } from '../../types';
import type { RiskLevel } from '../../types';
import type { ValidacaoDayJson } from '../../api/validacao';

// Past-day risk cells (Moderado+ only, faint, canvas-rendered) with that
// day's real ignitions on top: filled = the cell's morning level; red ring =
// the fire started in a cell we had marked Baixo (a documented miss).

interface Props {
  day: ValidacaoDayJson;
  showFirms?: boolean;
}

export default function ValidacaoDayLayer({ day, showFirms = false }: Props) {
  const map = useMap();
  const groupRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (groupRef.current) map.removeLayer(groupRef.current);
    const renderer = L.canvas({ padding: 0.5 });
    const group = L.layerGroup();

    for (const [lat, lon, prob, level] of day.cells) {
      const color = RISK_COLORS[(level as RiskLevel) in RISK_COLORS ? (level as RiskLevel) : 'Low'];
      group.addLayer(
        L.circleMarker([lat, lon], {
          renderer,
          radius: 3,
          fillColor: color,
          fillOpacity: 0.18 + prob * 0.3,
          stroke: false,
        }),
      );
    }

    if (showFirms) {
      for (const [lat, lon] of day.firms) {
        group.addLayer(
          L.circleMarker([lat, lon], {
            renderer,
            radius: 2.5,
            fillColor: '#94a3b8',
            fillOpacity: 0.7,
            stroke: false,
          }),
        );
      }
    }

    for (const fire of day.fires) {
      const missed = fire.level === 'Low' || fire.level == null;
      const fill = fire.level && fire.level in RISK_COLORS
        ? RISK_COLORS[fire.level as RiskLevel]
        : '#64748b';
      const marker = L.circleMarker([fire.lat, fire.lon], {
        renderer,
        radius: 6,
        fillColor: fill,
        fillOpacity: 0.95,
        stroke: true,
        color: missed ? '#ef4444' : '#f8fafc',
        weight: missed ? 2.5 : 1.5,
      });
      const levelTxt = fire.level ?? 'fora da grelha';
      const probTxt = fire.prob != null ? ` (${(fire.prob * 100).toFixed(1)}%)` : '';
      marker.bindTooltip(
        `${fire.concelho ?? 'Ignição'}: célula ${levelTxt}${probTxt}${missed ? ' · FALHA' : ''}`,
        { direction: 'top', offset: [0, -6] },
      );
      group.addLayer(marker);
    }

    group.addTo(map);
    groupRef.current = group;
    return () => {
      if (groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [day, map, showFirms]);

  return null;
}
